import React, { useRef } from 'react';
import { Card, Button, Space, App } from 'antd';
import { logger } from '../../services/logger.client';

interface DataManagementPanelProps {
  /** Triggers a full store-state export as a downloadable JSON file. */
  onExport: () => void;
  /**
   * Called with the parsed JSON object from the user's import file.
   * The container is responsible for validation, confirmation, and store mutation.
   */
  onImportData: (parsedData: Record<string, unknown>) => Promise<void>;
  /** Triggers the database reset flow (confirmation + API call are in the container). */
  onReset: () => void;
}

/**
 * Pure presenter for the Data Management card (ADR-034).
 *
 * Renders export, import, and reset buttons. Handles only local UI concerns:
 * - Opening the hidden file input on "Import Data" click
 * - Reading the selected file and parsing its JSON
 * - Delegating the parsed data to the container via `onImportData`
 *
 * Zero store access. Zero API calls.
 */
const DataManagementPanel: React.FC<DataManagementPanelProps> = ({
  onExport,
  onImportData,
  onReset,
}) => {
  const { message } = App.useApp();
  const importInputRef = useRef<HTMLInputElement>(null);

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  /** Reads the selected file, parses JSON, and delegates to the container. */
  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string) as Record<string, unknown>;
        onImportData(parsed).catch((err: Error) => {
          message.error(err.message);
        });
      } catch (error) {
        message.error('Error parsing file.');
        logger.error({ error }, 'Error parsing file during import.');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input for next import
  };

  return (
    <Card title="Data Management">
      <Space wrap>
        <Button onClick={onExport}>Export Data</Button>
        <Button onClick={handleImportClick}>Import Data</Button>
        <Button danger onClick={onReset}>Reset All Data</Button>
        <input
          type="file"
          ref={importInputRef}
          onChange={handleImportFile}
          accept=".json,application/json"
          style={{ display: 'none' }}
        />
      </Space>
    </Card>
  );
};

export default DataManagementPanel;